
import PageLayout from "@/layouts/PageLayout";
import { Shield, Lock, Server, AlertCircle, CheckCircle2 } from "lucide-react";
import { BackgroundGradient } from "@/components/ui/background-gradient";
import { Card, CardContent } from "@/components/ui/card";

const SecurityPage = () => {
  return (
    <PageLayout>
      <BackgroundGradient className="py-16 px-6 flex-grow" variant="subtle">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-display font-medium mb-8">Security Practices</h1>
          
          <p className="text-xl text-muted-foreground mb-12">
            At cheqbooq, we are committed to implementing robust security practices to protect your financial data 
            and personal information when our platform launches.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <Card className="bg-background border-border/40">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Lock className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-medium">Data Encryption</h3>
                <p className="text-muted-foreground">
                  All sensitive data will be encrypted both in transit and at rest using industry-standard encryption 
                  protocols, including TLS 1.3 for data in transit and AES-256 for data at rest.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-background border-border/40">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Server className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-medium">Secure Infrastructure</h3>
                <p className="text-muted-foreground">
                  Our infrastructure is designed to meet high security standards with secure cloud hosting,
                  regular security monitoring, and access controls.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-background border-border/40">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-medium">Threat Detection</h3>
                <p className="text-muted-foreground">
                  We plan to implement advanced monitoring systems to detect suspicious activities 
                  and potential security threats, with appropriate response procedures.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-background border-border/40">
              <CardContent className="p-6 space-y-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-medium">Regular Security Reviews</h3>
                <p className="text-muted-foreground">
                  Our systems will undergo regular security reviews and assessments to identify 
                  and address potential vulnerabilities as we develop and launch our platform.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <h2 className="text-2xl font-display font-medium mt-12 mb-6">Our Security Commitment</h2>
          <div className="prose prose-lg max-w-none">
            <p>
              Security is a foundational principle in the development of cheqbooq. As we build our financial 
              data platform, we are implementing security considerations from the ground up. Our approach includes:
            </p>
            
            <ul>
              <li>
                <strong>Defense in Depth:</strong> We are designing multiple layers of security controls throughout our 
                systems and processes.
              </li>
              <li>
                <strong>Principle of Least Privilege:</strong> Access to data will be granted only to those who need it 
                to perform specific functions.
              </li>
              <li>
                <strong>Security by Design:</strong> Security considerations are being built into our development process 
                from the beginning.
              </li>
              <li>
                <strong>Continuous Improvement:</strong> Our security practices will evolve to address new 
                threats and incorporate improved technologies.
              </li>
            </ul>
            
            <h3 className="text-xl font-medium mt-8 mb-4">Regulatory Compliance</h3>
            <p>
              We are designing our systems and processes with relevant financial and data protection regulations in mind, 
              including applicable privacy laws and financial service regulations.
            </p>
            
            <h3 className="text-xl font-medium mt-8 mb-4">Development Phase Disclaimer</h3>
            <p>
              Please note that cheqbooq is currently in development. The security practices described here represent 
              our planned implementation for when the platform launches. We are committed to implementing these 
              security measures before handling any live financial data.
            </p>
            
            <h3 className="text-xl font-medium mt-8 mb-4">Reporting Security Concerns</h3>
            <p>
              If you have security concerns or questions, please contact us at security@cheqbooq.com.
            </p>
          </div>
        </div>
      </BackgroundGradient>
    </PageLayout>
  );
};

export default SecurityPage;
